// Lib imports
import * as _ from 'lodash';
import * as React from 'react';

// Core imports
import ScreenMode from 'unspoken-core/lib/enums/ScreenMode';
import AttractionExercise from 'unspoken-core/lib/models/AttractionExercise';
import Project from 'unspoken-core/lib/models/Project';
import Respondent from 'unspoken-core/lib/models/Respondent';
import { SwipeDirection, SwipedStimulus } from 'unspoken-core/lib/models/Stimulus';

// Local imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import API from '../Api';
import AttractionMobileStimulus from './AttractionMobileStimulus';

// Interface describing the AttractionExerciseContainer props
interface AttractionContainerProps {
  device: 'mobile' | 'desktop';
  exercise: AttractionExercise;
  skipTraining: boolean;
  respondent: Respondent;
  project: Project;
}

// Interface describing the AttractionExerciseContainer state
interface AttractionContainerState {
  redirect: string | null;
  screenOrientation: ScreenMode;
  shuffledStimuli: SwipedStimulus[];
}

// Component that renders the attraction stimuli as well as the header
class AttractionExerciseContainer extends React.Component<AttractionContainerProps, AttractionContainerState> {

  public state: AttractionContainerState = {
    redirect: null,
    screenOrientation: ScreenMode.NONE,
    shuffledStimuli: []
  };

  public render() {

    // if exercise is finished, link back to SSI study
    if (this.state.redirect) {
      window.location.href = this.state.redirect;
      return null;
    }

    // Check if a forced screenmode is selected, and if that screenmode is active
    if(this.props.exercise.screenMode !==  ScreenMode.NONE
      && this.props.exercise.screenMode !== this.state.screenOrientation) {
      return (
        <div className={'orientation-switch'}>
          <FontAwesomeIcon icon="sync-alt" className={'orientation-switch__icon'} />
          <div className={'orientation-switch__text'}>{
            this.props.exercise.screenMode === ScreenMode.LANDSCAPE ?
              this.props.exercise.translations.switch_to_landscape :
              this.props.exercise.translations.switch_to_portrait
          }</div>
        </div>
      );
    }

    /**
     * Add unique index to the stimuli, so that React doesn't
     * update the AttractionStimulus, but performs an unmount
     * and mount. Needed for HammerJS to setup correct listeners
     *
     * Then filter out the swiped stimuli and take first three to render
     */
    const renderStimuli = this.state.shuffledStimuli
      .map((stimulus, i) => ({ index: i, ...stimulus }))
      .filter(stimulus => stimulus.swiped === undefined)
      .slice(0, 3);

    return (
      <div className="attraction">

        {/* Render exercise question */}
        {this.props.exercise.exerciseQuestion && (
          <div 
            className="attraction__header"
            dangerouslySetInnerHTML={{
              __html: this.props.exercise.exerciseQuestion,
            }}
          />
        )}

        {/* Render exercise body with stimuli */}
        <div className="attraction__body">
          {renderStimuli.map((stimulus, index) => (
            <AttractionMobileStimulus
              active={index === 0}
              key={stimulus.index}
              leftStampOptions={this.props.exercise.stamp.left}
              rightStampOptions={this.props.exercise.stamp.right}
              onSwipe={this.onSwipe}
              stimulus={stimulus}
              timeout={this.props.exercise.exerciseTimeout}
              background={this.props.exercise.exerciseBackground}
              index={stimulus.index}
            />
          ))}
        </div>
      </div>
    );
  }

  public componentDidMount() {

    // Check the current orientation and set it in the state
    this.onWindowResize();

    // Attach listener to resize event to catch orientation changes
    window.addEventListener('resize', this.onWindowResize);

    // Extract the stimuli from the exercise design
    const route = this.props.exercise.design.routes[this.props.respondent.version];
    const stimuli: SwipedStimulus[] = [];
    route.stimuli.forEach( id => {
      const stimulus = this.props.project.stimuli.find( projectStimulus => projectStimulus.id === id );
        if ( stimulus ) {
          stimuli.push({
            ...stimulus,
            duration: -1,
            resets: 0,
          });
        }
    });

    this.setState({ shuffledStimuli: stimuli });
  }

  private onWindowResize = () => {
    // Get new orientation of window
    const windowOrientation = this.getWindowOrientation();

    // Check if it's different than the current orientation
    // If so, update the state orientation
    if(windowOrientation !== this.state.screenOrientation) {
      this.setComponentOrientation(windowOrientation);
    }
  }

  private getWindowOrientation(): ScreenMode {
    // Check current orientation
    const windowOrientation = window.innerWidth > window.innerHeight ? ScreenMode.LANDSCAPE : ScreenMode.PORTRAIT;

    return windowOrientation;
  }

  private setComponentOrientation =  (orientation: ScreenMode) => {
    // Set state with passed orientation
    this.setState({
      screenOrientation: orientation
    });
  }

  // Listen to swipe event to update the stimulus and load the next one
  private onSwipe = (index: number, direction: SwipeDirection, resets: number, duration: number) => {

    // save the data in the swiped stimulus
    const newStimuli = _.cloneDeep(this.state.shuffledStimuli);
    newStimuli[index].swiped = direction;
    newStimuli[index].resets = resets;
    newStimuli[index].duration = duration;

    // update the stimuli state
    this.setState({ shuffledStimuli: newStimuli }, () => {
      // if the last stimuli has been swiped, save data
      const stimuliLeft = this.state.shuffledStimuli.filter(stimulus => stimulus.swiped === undefined);
      if (stimuliLeft.length === 0) {
        this.saveExerciseData(this.state.shuffledStimuli);
      }
    });
  }

  private saveExerciseData = async (swipedStimuli: SwipedStimulus[]) => {
    const respondent = this.props.respondent;
    respondent.swipedStimuli = swipedStimuli;

    // save data
    if ( respondent.id ) {
      await API.respondents.update(
        respondent,
        this.props.exercise.id,
        this.props.project.id,
      );
    }

    // redirect to SSI
    const params = {
      studyName: this.props.exercise.ssiSettings.name,
      'sys_skipto': this.props.exercise.ssiSettings.linkBack,
      [this.props.exercise.ssiSettings.respondentId]: this.props.respondent.id,
      [this.props.exercise.ssiSettings.password]: this.props.respondent.password,
    };
    const linkbackUrl = this.props.exercise.ssiSettings.url + '?' + Object.keys(params).map(key => `${key}=${params[key]}`).join('&');

    this.setState({ redirect: linkbackUrl });
  }
}

export default AttractionExerciseContainer;



// WEBPACK FOOTER //
// ./src/components/AttractionExerciseContainer.tsx